<script setup lang="ts">
import { h, computed, VNode, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useVaultStore, type VaultItem, type VaultListingSort } from '@/stores/vaultStore';
import { groupVaultKeys } from '@/utils/vaultSelection';
import { useAuthStore } from '@/stores/auth';
import { isOfficeAddinHost } from '@/office/utils';
import { useDialog, NTooltip, NIcon, NTag, type DropdownOption } from 'naive-ui';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaVersionBadge from '@/components/design/base/GaiaVersionBadge.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaRiskTint,
  gaiaStatus,
} from '@/components/design/themes/tokens';
import {
  IconArchive,
  IconArchiveFile,
  IconDelete,
  IconDocument,
  IconDocumentAdd,
  IconDocumentLock,
  IconDocumentWord,
  IconDocuments,
  IconDownload,
  IconEdit,
  IconEye,
  IconFolder,
  IconFolderOpen,
  IconHome,
  IconMore,
  IconPaste,
  IconSearch,
  IconSparkles,
  IconTime,
  IconUpload,
  IconUser,
  IconShare,
  IconSign,
} from '@/components/design/icons';
import UserInitialsAvatar from '@/components/UserInitialsAvatar.vue';
import {
  DEFAULT_FILE_VAULT_COLUMNS,
  type FileVaultColumnKey,
} from '@/components/file_vault/fileVaultColumns';
import {
  DEFAULT_FILE_VAULT_ACTIONS,
  type FileVaultActionKey,
} from '@/components/file_vault/fileVaultActions';
import GaiaContractStateCell from '@/components/design/domain/contractManagement/GaiaContractStateCell.vue';
import {
  CONTRACT_ICON_STATES,
  type ContractIconState,
} from '@/components/design/domain/contractManagement/contractStateIcons';
import { isArchive, isConversionInProgress, isPdfVaultFile } from '@/utils/fileTypeUtils.ts';
import { setVaultFileDragData } from '@/utils/vaultFileDrag';
import {
  isOsFileDrag,
  readDroppedFiles,
  type DroppedFile,
  type VaultFileDropTarget,
} from '@/utils/vaultFileDrop';
import { PreprocessingTaskStatus } from '@/api';
import { useRouter } from 'vue-router';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { vaultActionBlock } from '@/components/file_vault/vaultActionPermissions';
import ContractFilesPopover from '@/components/file_vault/ContractFilesPopover.vue';
import { vaultTypeLabel } from '@/services/vaultListingService';
import { ACCESS_PERMISSION_WRITE, allows, deniedReason } from '@/utils/accessPermission';

const message = useGaiaToast();
const dialog = useDialog();
const router = useRouter();

// Store
const vaultStore = useVaultStore();
const authStore = useAuthStore();

// Props
const props = withDefaults(
  defineProps<{
    items: VaultItem[];
    loading: boolean;
    selectedKeys: string[];
    /**
     * Whether the active organisation has a GAIA integration.
     * Enables exporting vault files to CLM sections.
     */
    hasGaiaIntegration?: boolean;
    /**
     * Offers "Convert to contract" on plain files. Off in the system vault,
     * whose files never become contracts.
     */
    canConvertToContract?: boolean;
    /**
     * Offers "Share" on folders, plain files and contracts. Off in the system
     * vault, where nothing can be shared.
     */
    canShare?: boolean;
    /** Whether the listing shows inline-search results (adapts the empty state). */
    isSearchResults?: boolean;
    /**
     * The listed directories are pinned platform folders (the system vault's top two
     * levels): no rename, move, delete or owner change. Decided by location, since the
     * API no longer flags them.
     */
    lockDirectories?: boolean;
    /**
     * Disable drag-and-drop moves. The system vault accepts no incoming moves, so every
     * drop inside it would be refused; moving out goes through the move dialog.
     */
    disableDrag?: boolean;
    /**
     * Take files and folders dragged in from the OS: onto a folder's or a
     * contract's name, or anywhere else on the table for the current folder.
     * The list only reads the drop and says where it landed (`files-dropped`).
     */
    fileDrop?: boolean;
    /** Why the table-wide drop (into the current folder) is off; null when it is on. */
    fileDropHereBlocked?: string | null;
    /**
     * Which page of the listing `items` is, when the server pages it (a
     * folder). Null when `items` holds everything (search results), which
     * then pages here.
     */
    pagination?: { page: number; pageSize: number; total: number } | null;
    /**
     * Available row actions. Each row keeps the actions that apply to it,
     * sorted by label with its type's trailing actions last; the last
     * `inlineActionCount` of those render as inline icon buttons, everything
     * before folds into the "⋯" menu.
     * The office layout keeps its own order/split but respects membership.
     */
    actions?: FileVaultActionKey[];
    /** Per row, how many trailing applicable actions render as icon buttons. */
    inlineActionCount?: number;
    /** Visible columns, in display order. */
    columns?: FileVaultColumnKey[];
    /** Number of leading columns frozen (sticky) on the left. Ignored in the office layout. */
    frozenLeft?: number;
    /** Number of trailing columns frozen (sticky) on the right. Ignored in the office layout. */
    frozenRight?: number;
  }>(),
  {
    hasGaiaIntegration: false,
    canConvertToContract: false,
    canShare: false,
    isSearchResults: false,
    lockDirectories: false,
    disableDrag: false,
    fileDrop: false,
    fileDropHereBlocked: null,
    pagination: null,
    actions: () => [...DEFAULT_FILE_VAULT_ACTIONS],
    // 0 = the default vault folds every row action into a single "⋯" menu.
    inlineActionCount: 0,
    columns: () => [...DEFAULT_FILE_VAULT_COLUMNS],
    frozenLeft: 0,
    frozenRight: 4,
  }
);

const hasAction = (key: FileVaultActionKey): boolean => props.actions.includes(key);

// Local state for the flat list view.
// Navigation is breadcrumb-based (the parent replaces `items` when opening a
// folder), so the list is always a single flat level — no inline tree
// expansion. The table is virtualized so thousands of rows stay cheap.
const draggedItem = ref<VaultItem | null>(null);
const dropTargetKey = ref<string | null>(null);
const isDragging = ref(false);
const homeDropZoneActive = ref(false);
const dragTimeoutId = ref<number | null>(null);
const showHomeDropZone = ref(false);
const homeDirectory = computed<VaultItem | null>(() => vaultStore.directoryPath[0] ?? null);
const canDropToHome = computed<boolean>(
  () => hasAction('move') && !props.disableDrag && vaultStore.directoryPath.length > 1
);

function getCompactActionsMenuProps(): { class: string } {
  return {
    class: 'file-vault-compact-actions-menu',
  };
}

// Watch for isDragging changes to handle the Home drop zone visibility with delay
watch(
  () => isDragging.value,
  newValue => {
    // Clear any existing timeout for home drop zone
    if (dragTimeoutId.value) {
      clearTimeout(dragTimeoutId.value);
    }

    if (newValue) {
      // Show home drop zone after a short delay to avoid flashing for quick drag operations
      dragTimeoutId.value = window.setTimeout(() => {
        if (isDragging.value) {
          // Double-check we're still dragging
          showHomeDropZone.value = true;
        }
      }, 200);
    } else {
      // Hide immediately when drag ends
      showHomeDropZone.value = false;
      homeDropZoneActive.value = false;
    }
  }
);

// Emits
const emit = defineEmits<{
  'selection-change': [keys: string[]];
  'item-click': [item: VaultItem];
  'item-dblclick': [item: VaultItem];
  rename: [item: VaultItem];
  move: [item: VaultItem];
  'reassign-owner': [item: VaultItem];
  share: [item: VaultItem];
  'insert-into-word': [item: VaultItem];
  'item-moved': [item: VaultItem, targetDirectory: string, currentDirectoryUid: string | null];
  'download-folder': [item: VaultItem];
  unpack: [item: VaultItem];
  'convert-to-docx': [item: VaultItem];
  preview: [item: VaultItem];
  'export-to-gaia': [item: VaultItem];
  'convert-to-contract': [item: VaultItem];
  'ask-ai': [item: VaultItem];
  'add-file': [item: VaultItem];
  'update:pagination': [value: { page: number; pageSize: number }];
  'files-dropped': [target: VaultFileDropTarget, files: DroppedFile[]];
}>();

// Vault access is inherited through shares the listing doesn't carry, so the
// UI can't tell what the user may do with an item: actions stay enabled and
// the backend's refusal is shown. Only the certain rules are applied here.

/**
 * Change owner: a single file through its own update (ADMIN on the file);
 * contracts and folders only through the bulk endpoint, which is org admins
 * only.
 */
function canReassignOwner(row: VaultItem): boolean {
  return authStore.userPermissions.is_org_admin || row.type === 'file';
}

// Pinned platform directories (the system vault's Templates / Intake Agent and
// their per-template / per-agent folders) can't be renamed, moved, deleted, or have
// their owner reassigned — the backend rejects the first three with a 422;
// change-owner isn't guarded server-side yet, so this is enforced defensively too.
function isSystemDirectory(row: VaultItem): boolean {
  return row.type === 'directory' && props.lockDirectories;
}

/** The badge palettes, one per tone — the shared status tokens, so these
    pills match every other status chip in the app. */
const STATUS_TONES = {
  neutral: {
    bg: gaiaNeutral.surfaceMuted,
    color: gaiaNeutral.textTertiary,
    dot: gaiaNeutral.textMuted,
  },
  warning: { bg: gaiaRiskTint.warningSurface, color: gaiaStatus.warning, dot: gaiaStatus.warning },
  success: {
    bg: gaiaRiskTint.successSurface,
    color: gaiaStatus.successText,
    dot: gaiaStatus.success,
  },
  alert: { bg: gaiaStatus.alertSurface, color: gaiaStatus.alert, dot: gaiaStatus.alert },
} as const;

type StatusValue = PreprocessingTaskStatus | null | undefined;

function isDocxVaultFile(row: VaultItem): boolean {
  if (row.type !== 'file') return false;
  return (
    row.contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    /\.docx$/i.test(row.name)
  );
}

// Only while a DOCX conversion is queued or running; a failed one is part of
// the row's Error badge.
function shouldShowConversionIndicator(row: VaultItem): boolean {
  return row.type === 'file' && isConversionInProgress(row);
}

const UNFINISHED_STATUSES = new Set<StatusValue>([
  null,
  undefined,
  PreprocessingTaskStatus.PENDING,
  PreprocessingTaskStatus.RUNNING,
]);

/**
 * Where a file converting into a contract is: still being read in (text
 * extraction not finished, or the preview still running), or past that and
 * being analyzed into the contract itself.
 */
function conversionPhase(row: VaultItem): 'importing' | 'analyzing' {
  const previewRunning =
    row.previewStatus === PreprocessingTaskStatus.PENDING ||
    row.previewStatus === PreprocessingTaskStatus.RUNNING;
  return UNFINISHED_STATUSES.has(row.ocrStatus) || previewRunning ? 'importing' : 'analyzing';
}

/** An archive: kept as is, unpacked to work with its files. */
function isArchiveRow(row: VaultItem): boolean {
  return (
    row.type === 'file' &&
    (row.typeLabel === 'archive' ||
      isArchive(row.contentType, row.name) ||
      row.ocrStatus === PreprocessingTaskStatus.ARCHIVE ||
      row.previewStatus === PreprocessingTaskStatus.ARCHIVE)
  );
}

const PROCESSING_STEPS = [
  { field: 'ocrStatus', name: 'Text extraction' },
  { field: 'previewStatus', name: 'Preview generation' },
  { field: 'conversionStatus', name: 'DOCX conversion' },
] as const;

/**
 * Anything off in the file's processing, one line per step. The API only says
 * which step failed or warned, not why, so that is what the badge's hover
 * says. Null when all is well — the row then shows no badge at all; an error
 * outranks a warning.
 */
function processingIssue(row: VaultItem): { level: 'error' | 'warning'; lines: string[] } | null {
  if (row.type !== 'file') return null;
  const errors: string[] = [];
  const warnings: string[] = [];
  for (const { field, name } of PROCESSING_STEPS) {
    const status = row[field];
    switch (status) {
      case PreprocessingTaskStatus.FAILED: {
        errors.push(`${name} failed`);
        break;
      }
      case PreprocessingTaskStatus.WARNING: {
        warnings.push(`${name} finished with warnings`);
        break;
      }
      case PreprocessingTaskStatus.PARTIAL:
        {
          warnings.push(`${name} only partly succeeded`);
          // No default
        }
        break;
    }
  }
  if (errors.length > 0) return { level: 'error', lines: [...errors, ...warnings] };
  if (warnings.length > 0) return { level: 'warning', lines: warnings };
  return null;
}

function getConversionIndicatorConfig(
  status: StatusValue
): { label: string; tooltip: string; bg: string; color: string; icon: string } | null {
  switch (status) {
    case PreprocessingTaskStatus.PENDING:
      return {
        label: 'Queued',
        tooltip: 'DOCX conversion is queued.',
        bg: STATUS_TONES.neutral.bg,
        color: STATUS_TONES.neutral.color,
        icon: STATUS_TONES.neutral.dot,
      };
    case PreprocessingTaskStatus.RUNNING:
      return {
        label: 'Converting',
        tooltip: 'DOCX conversion is in progress.',
        bg: STATUS_TONES.warning.bg,
        color: STATUS_TONES.warning.color,
        icon: STATUS_TONES.warning.dot,
      };
    case PreprocessingTaskStatus.FAILED:
      return {
        label: 'Failed',
        tooltip: 'DOCX conversion failed.',
        bg: STATUS_TONES.alert.bg,
        color: STATUS_TONES.alert.color,
        icon: STATUS_TONES.alert.dot,
      };
    default:
      return null;
  }
}

function renderConversionIndicator(row: VaultItem): VNode | null {
  if (!shouldShowConversionIndicator(row)) return null;

  const config = getConversionIndicatorConfig(row.conversionStatus);
  if (!config) return null;

  return h(
    NTooltip,
    { trigger: 'hover', placement: 'top' },
    {
      trigger: () =>
        h(
          'span',
          {
            style: `display:inline-flex; align-items:center; gap:4px; flex-shrink:0; height:18px; margin-left:6px; padding:0 6px; border-radius:999px; background:${config.bg}; color:${config.color}; font-size:10px; font-weight:600; line-height:18px; white-space:nowrap; cursor:default;`,
          },
          [
            h(
              NIcon,
              { size: 11, style: `color:${config.icon}; flex-shrink:0;` },
              { default: () => h(IconDocumentWord) }
            ),
            h('span', null, config.label),
          ]
        ),
      default: () => config.tooltip,
    }
  );
}

// Folder item-count badge. The count is recursive (plain files and contracts in
// the folder plus every subfolder, a contract counting once) and is supplied by the directory listing endpoint, so it
// stays live through the store refresh that follows moves/uploads/deletes.
// Populated folders pick up the folder-blue accent; empty folders stay muted so
// they visually recede. Colours are translucent so the pill reads well on both
// light and dark surfaces. NTag's `color` prop keeps this Naive-UI-idiomatic.
function renderFolderCountBadge(row: VaultItem): VNode | null {
  if (row.type !== 'directory' || typeof row.fileCount !== 'number') return null;

  const count = row.fileCount;
  const empty = count === 0;
  const tooltip = empty
    ? 'Empty folder'
    : `${count} ${count === 1 ? 'item' : 'items'} (including subfolders)`;

  const color = empty
    ? { bg: gaiaNeutral.surfaceMuted, fg: gaiaNeutral.textFaint }
    : { bg: gaiaAccent.tint, fg: gaiaAccent.base };
  const office = isOfficeAddinHost();

  return h(
    NTooltip,
    { trigger: 'hover', placement: 'top' },
    {
      trigger: () =>
        h(
          NTag,
          {
            size: 'small',
            round: true,
            bordered: false,
            color: { color: color.bg, textColor: color.fg },
            style: `flex-shrink: 0; margin-left: 8px; height: ${office ? '17px' : '20px'}; font-weight: 600;`,
          },
          {
            // Render the icon + count together in the default slot (rather than
            // NTag's `icon` slot) so the spacing is a single controlled gap and
            // the rounded pill stays balanced around its content.
            default: () =>
              h(
                'span',
                {
                  style: `display: inline-flex; align-items: center; gap: 4px; font-size: ${office ? '10px' : '11.5px'}; line-height: 1; letter-spacing: 0.01em;`,
                },
                [
                  h(NIcon, { size: office ? 11 : 13 }, { default: () => h(IconDocuments) }),
                  h('span', String(count)),
                ]
              ),
          }
        ),
      default: () => tooltip,
    }
  );
}

type OfficeActionKey =
  | 'open-folder'
  | 'download-folder'
  | 'preview'
  | 'unpack'
  | 'convert-to-docx'
  | 'download'
  | 'versions'
  | 'rename'
  | 'move'
  | 'export-to-gaia'
  | 'change-owner'
  | 'share'
  | 'insert-into-word'
  | 'convert-to-contract'
  | 'ask-ai'
  | 'add-file'
  | 'sign'
  | 'delete';

function createDropdownIcon(icon: Record<string, unknown>): () => VNode {
  return () => h(NIcon, { size: 16 }, { default: () => h(icon) });
}

const ACTION_ICONS: Record<FileVaultActionKey, Record<string, unknown>> = {
  'ask-ai': IconSparkles,
  preview: IconEye,
  delete: IconDelete,
  download: IconDownload,
  'download-folder': IconDownload,
  versions: IconTime,
  'convert-to-docx': IconDocumentWord,
  'convert-to-contract': IconDocumentLock,
  'export-to-gaia': IconUpload,
  sign: IconSign,
  rename: IconEdit,
  move: IconFolderOpen,
  'change-owner': IconUser,
  share: IconShare,
  'add-file': IconDocumentAdd,
};

function getConvertToDocxOption(row: VaultItem): DropdownOption {
  const conversionInProgress = isConversionInProgress(row);
  return {
    key: 'convert-to-docx',
    label: conversionInProgress ? 'Conversion in progress' : 'Convert to DOCX',
    icon: createDropdownIcon(IconDocumentWord),
    disabled: conversionInProgress,
  };
}

// Menu option for one configured action, or null when it doesn't apply to the
// row. Selection is handled by handleOfficeActionSelect so the action logic
// lives in one place.
/**
 * What the backend refuses (409) while a file converts into a contract. The
 * entries stay in the menu, disabled, so the row says why they are missing.
 */
const BLOCKED_WHILE_CONVERTING = new Set<string>([
  'rename',
  'move',
  'delete',
  'change-owner',
  'convert-to-docx',
  'convert-to-contract',
]);

/**
 * Why an action is unavailable on a row: the file is converting, or the
 * caller's bits on it lack what the backend checks. Null when it is available.
 */
function actionBlock(row: VaultItem, key: string): string | null {
  if (row.converting && BLOCKED_WHILE_CONVERTING.has(key)) {
    return 'Becoming a contract — wait until it is done';
  }
  return vaultActionBlock(row, key);
}

/** Disables an option the row can't take, with the reason as its title. */
function blockAction(row: VaultItem, option: DropdownOption): DropdownOption {
  const reason = actionBlock(row, String(option.key));
  return reason ? { ...option, disabled: true, props: { title: reason } } : option;
}

function getActionMenuOption(key: FileVaultActionKey, row: VaultItem): DropdownOption | null {
  const option = buildActionMenuOption(key, row);
  return option ? blockAction(row, option) : null;
}

/** What a contract row offers; double-click opens the contract itself. */
const CONTRACT_ROW_ACTIONS = new Set<FileVaultActionKey>([
  'sign',
  'add-file',
  'move',
  'share',
  'change-owner',
  'delete',
]);

function buildActionMenuOption(key: FileVaultActionKey, row: VaultItem): DropdownOption | null {
  if (row.type === 'contract' && !CONTRACT_ROW_ACTIONS.has(key)) return null;
  const isFile = row.type === 'file';
  switch (key) {
    case 'ask-ai':
      // Files only — the chat resolves references to concrete revisions and the backend
      // has no field for a directory. Archives are excluded the same way: the pipeline
      // never extracts their text, so the chat can't read them until they're unpacked.
      // A draft has no extracted text either until it is saved.
      return isFile && !isArchive(row.contentType, row.name) && !row.isDraft
        ? { key: 'ask-ai', label: 'Ask AI', icon: createDropdownIcon(IconSparkles) }
        : null;
    case 'preview':
      if (!isFile) return null;
      if (isArchive(row.contentType, row.name)) {
        return { key: 'unpack', label: 'Unpack', icon: createDropdownIcon(IconArchive) };
      }
      return {
        key: 'preview',
        label: row.previewStatus === PreprocessingTaskStatus.DONE ? 'Preview' : 'Preview not ready',
        icon: createDropdownIcon(IconEye),
        disabled: row.previewStatus !== PreprocessingTaskStatus.DONE,
      };
    case 'delete':
      return {
        key: 'delete',
        label: 'Delete',
        icon: createDropdownIcon(IconDelete),
        // System directories can never be deleted.
        disabled: isSystemDirectory(row),
      };
    case 'download':
      return isFile
        ? { key: 'download', label: 'Download', icon: createDropdownIcon(IconDownload) }
        : null;
    case 'download-folder':
      return isFile
        ? null
        : {
            key: 'download-folder',
            label: 'Download as zip',
            icon: createDropdownIcon(IconDownload),
          };
    case 'versions':
      return isFile
        ? { key: 'versions', label: 'Version history', icon: createDropdownIcon(IconTime) }
        : null;
    case 'convert-to-docx':
      return isPdfVaultFile(row) ? getConvertToDocxOption(row) : null;
    case 'convert-to-contract':
      // An archive is unpacked, never made a contract; a draft becomes one when it is saved.
      return isFile && props.canConvertToContract && !isArchiveRow(row) && !row.isDraft
        ? {
            key: 'convert-to-contract',
            label: 'Convert to contract',
            icon: createDropdownIcon(IconDocumentLock),
          }
        : null;
    case 'export-to-gaia':
      return isFile && props.hasGaiaIntegration
        ? {
            key: 'export-to-gaia',
            label: 'Export to Gaia CLM Section',
            icon: createDropdownIcon(IconUpload),
          }
        : null;
    case 'sign':
      return authStore.userPermissions.can_use_signing &&
        (row.type === 'contract' || (isFile && !isArchiveRow(row) && !row.isDraft))
        ? { key: 'sign', label: 'Sign this', icon: createDropdownIcon(IconSign) }
        : null;
    case 'rename':
      return {
        key: 'rename',
        label: 'Rename',
        icon: createDropdownIcon(IconEdit),
        // System directories can never be renamed.
        disabled: isSystemDirectory(row),
      };
    case 'move':
      return {
        key: 'move',
        label: 'Move to…',
        icon: createDropdownIcon(IconFolderOpen),
        // System directories can never be moved.
        disabled: isSystemDirectory(row),
      };
    case 'share':
      return props.canShare
        ? { key: 'share', label: 'Share', icon: createDropdownIcon(IconShare) }
        : null;
    case 'add-file':
      return row.type === 'contract'
        ? { key: 'add-file', label: 'Add a file', icon: createDropdownIcon(IconDocumentAdd) }
        : null;
    case 'change-owner':
      return canReassignOwner(row) && !isSystemDirectory(row)
        ? { key: 'change-owner', label: 'Change owner', icon: createDropdownIcon(IconUser) }
        : null;
  }
}

/**
 * The actions each row type moves to the end of its otherwise alphabetical
 * list, in this order — with three inline buttons, these are the row's icons.
 */
const TRAILING_ROW_ACTIONS: Record<VaultItem['type'], FileVaultActionKey[]> = {
  file: ['preview', 'share', 'download'],
  directory: ['rename', 'share', 'download-folder'],
  contract: ['add-file', 'share', 'sign'],
};

// A row's applicable actions, split for the standard layout: sorted by label
// with the row type's trailing actions last, the final `inlineActionCount` of
// them render as inline icon buttons and everything before folds into the
// "⋯" menu.
function splitRowActions(row: VaultItem): {
  menuOptions: DropdownOption[];
  buttonActions: { key: FileVaultActionKey; option: DropdownOption }[];
} {
  const trailing = TRAILING_ROW_ACTIONS[row.type];
  const applicable = props.actions
    .flatMap(key => {
      const option = getActionMenuOption(key, row);
      return option ? [{ key, option }] : [];
    })
    // Non-trailing actions rank -1, so they sort first, alphabetically.
    .sort(
      (a, b) =>
        trailing.indexOf(a.key) - trailing.indexOf(b.key) ||
        String(a.option.label).localeCompare(String(b.option.label))
    );
  const splitAt = Math.max(0, applicable.length - props.inlineActionCount);
  const menuEntries = applicable.slice(0, splitAt);
  // Delete is isolated at the bottom of the menu behind a divider.
  const deleteEntry = menuEntries.find(entry => entry.key === 'delete');
  const otherOptions = menuEntries.filter(entry => entry !== deleteEntry).map(e => e.option);
  const menuOptions = deleteEntry
    ? otherOptions.length > 0
      ? [...otherOptions, { key: 'delete-divider', type: 'divider' }, deleteEntry.option]
      : [deleteEntry.option]
    : otherOptions;
  return {
    menuOptions,
    buttonActions: applicable.slice(splitAt),
  };
}

function getOfficeActionOptions(row: VaultItem): DropdownOption[] {
  return buildOfficeActionOptions(row).map(option => blockAction(row, option));
}

function buildOfficeActionOptions(row: VaultItem): DropdownOption[] {
  if (row.type === 'contract') {
    return props.actions
      .filter(key => CONTRACT_ROW_ACTIONS.has(key))
      .flatMap((key): DropdownOption[] => {
        const option = buildActionMenuOption(key, row);
        return option ? [option] : [];
      });
  }
  if (row.type === 'directory') {
    const writable = !isSystemDirectory(row);
    const options: DropdownOption[] = [
      {
        key: 'open-folder',
        label: 'Open folder',
        icon: createDropdownIcon(IconFolderOpen),
      },
      {
        key: 'download-folder',
        label: 'Download as zip',
        icon: createDropdownIcon(IconDownload),
      },
      {
        key: 'rename',
        label: 'Rename',
        icon: createDropdownIcon(IconEdit),
        disabled: !writable,
      },
      ...(hasAction('move')
        ? [
            {
              key: 'move',
              label: 'Move to…',
              icon: createDropdownIcon(IconFolderOpen),
              disabled: !writable,
            },
          ]
        : []),
    ];
    if (hasAction('share') && props.canShare) {
      options.push({ key: 'share', label: 'Share', icon: createDropdownIcon(IconShare) });
    }
    if (hasAction('change-owner') && canReassignOwner(row) && !isSystemDirectory(row)) {
      options.push({
        key: 'change-owner',
        label: 'Change owner',
        icon: createDropdownIcon(IconUser),
      });
    }
    if (hasAction('delete')) {
      options.push({
        key: 'delete',
        label: 'Delete',
        icon: createDropdownIcon(IconDelete),
        disabled: isSystemDirectory(row),
      });
    }
    return options;
  }

  const archiveFile = isArchive(row.contentType, row.name);
  const options: DropdownOption[] = [
    ...(archiveFile
      ? [
          {
            key: 'unpack',
            label: 'Unpack',
            icon: createDropdownIcon(IconArchive),
          },
        ]
      : []),
    {
      key: 'download',
      label: 'Download',
      icon: createDropdownIcon(IconDownload),
    },
    ...(hasAction('ask-ai')
      ? [{ key: 'ask-ai', label: 'Ask AI', icon: createDropdownIcon(IconSparkles) }]
      : []),
    ...(hasAction('versions')
      ? [
          {
            key: 'versions',
            label: 'Version history',
            icon: createDropdownIcon(IconTime),
          },
        ]
      : []),
    ...(isPdfVaultFile(row) ? [getConvertToDocxOption(row)] : []),
    {
      key: 'rename',
      label: 'Rename',
      icon: createDropdownIcon(IconEdit),
    },
    ...(hasAction('move')
      ? [
          {
            key: 'move',
            label: 'Move to…',
            icon: createDropdownIcon(IconFolderOpen),
          },
        ]
      : []),
  ];

  if (props.canConvertToContract && !row.isDraft) {
    options.push({
      key: 'convert-to-contract',
      label: 'Convert to contract',
      icon: createDropdownIcon(IconDocumentLock),
    });
  }

  if (props.hasGaiaIntegration) {
    options.push({
      key: 'export-to-gaia',
      label: 'Export to Gaia CLM Section',
      icon: createDropdownIcon(IconUpload),
    });
  }

  if (isOfficeAddinHost() && isDocxVaultFile(row)) {
    options.splice(3, 0, {
      key: 'insert-into-word',
      label: 'Insert into current document',
      icon: createDropdownIcon(IconPaste),
    });
  }

  if (hasAction('share') && props.canShare) {
    options.push({ key: 'share', label: 'Share', icon: createDropdownIcon(IconShare) });
  }

  if (hasAction('change-owner') && canReassignOwner(row)) {
    options.push({
      key: 'change-owner',
      label: 'Change owner',
      icon: createDropdownIcon(IconUser),
    });
  }

  if (hasAction('delete')) {
    options.push({
      key: 'delete',
      label: 'Delete',
      icon: createDropdownIcon(IconDelete),
    });
  }

  return options;
}

async function handleOfficeActionSelect(key: string | number, row: VaultItem): Promise<void> {
  const action = String(key) as OfficeActionKey;

  switch (action) {
    case 'open-folder':
      if (row.type === 'directory') {
        emit('item-dblclick', row);
      }
      return;
    case 'download-folder':
      if (row.type === 'directory') {
        emit('download-folder', row);
      }
      return;
    case 'preview':
      if (row.type === 'file' && row.previewStatus === PreprocessingTaskStatus.DONE) {
        emit('preview', row);
      }
      return;
    case 'unpack':
      if (row.type === 'file') {
        emit('unpack', row);
      }
      return;
    case 'convert-to-docx':
      if (row.type === 'file' && isPdfVaultFile(row) && !isConversionInProgress(row)) {
        emit('convert-to-docx', row);
      }
      return;
    case 'download':
      emit('item-click', row);
      return;
    case 'versions':
      if (row.type === 'file') {
        await router.push(`/vault/file/${row.key.replace('file-', '')}/versions`);
      }
      return;
    case 'rename':
      emit('rename', row);
      return;
    case 'move':
      emit('move', row);
      return;
    case 'add-file':
      if (row.type === 'contract') emit('add-file', row);
      return;
    case 'sign':
      // The detail views' own entry points: a contract with all its files, a
      // file loose in the vault on its own (as the file dropper's "Sign documents").
      if (row.type === 'contract') {
        const contractUid = row.key.replace('contract-', '');
        await router.push({
          name: 'signing-contract-request-new',
          params: { uid: contractUid },
          query: { contract_uid: contractUid },
        });
      } else if (row.type === 'file') {
        await router.push({
          name: 'signing-request-new',
          query: { vault_file_uids: row.key.replace('file-', '') },
        });
      }
      return;
    case 'export-to-gaia':
      if (row.type === 'file') {
        emit('export-to-gaia', row);
      }
      return;
    case 'convert-to-contract':
      if (row.type === 'file') {
        emit('convert-to-contract', row);
      }
      return;
    case 'change-owner':
      emit('reassign-owner', row);
      return;
    case 'share':
      emit('share', row);
      return;
    case 'insert-into-word':
      emit('insert-into-word', row);
      return;
    case 'ask-ai':
      emit('ask-ai', row);
      return;
    case 'delete':
      dialog.warning({
        title: 'Delete',
        content: `Delete "${row.name}"? ${DELETE_CONSEQUENCE[row.type]} This cannot be undone.`,
        positiveText: 'Delete',
        negativeText: 'Cancel',
        positiveButtonProps: { type: 'error' },
        onPositiveClick: () => handleDeleteItem(row),
      });
      return;
    default:
      return;
  }
}

// Define columns for the data table

// ── Table shape ─────────────────────────────────────────────────────────────
//
// One grid track per visible column, in the order the host asked for. The Word
// task pane gets its own narrower set: the two prose columns are dropped and
// the actions collapse to a single menu.

const COLUMN_DEFS: Record<FileVaultColumnKey, GaiaDataColumn> = {
  name: { key: 'name', label: 'Name', width: 'minmax(220px,1fr)', sortable: true },
  type: { key: 'type', label: 'Type', width: '110px', sortable: true },
  lastModified: { key: 'lastModified', label: 'Last modified', width: '140px', sortable: true },
  owner: { key: 'owner', label: 'Owner', width: '100px', align: 'center', sortable: true },
  state: { key: 'state', label: 'Status', width: '220px', sortable: true },
  actions: { key: 'actions', label: 'Actions', width: '0px' },
};

const OFFICE_COLUMN_DEFS: Record<string, GaiaDataColumn> = {
  name: { key: 'name', label: 'Name', width: 'minmax(150px,1fr)', sortable: true },
  owner: { key: 'owner', label: 'Owner', width: '80px', align: 'center' },
};

/** Visible columns, minus `actions` — GaiaDataTable owns that track itself. */
/** Body shimmer while a listing or search is in flight — same idiom as the
    CM tables, so the vault doesn't sit blank (or worse, claim "no results")
    for the debounce window + request time. */
const skeletonWidths = ['80px', '64%', '96px', '72px', '32px', '54%'];
const skeletonCells = computed(() =>
  tableColumns.value.map((_, position) => skeletonWidths[position % skeletonWidths.length])
);

const tableColumns = computed<GaiaDataColumn[]>(() => {
  const defs = isOfficeAddinHost() ? OFFICE_COLUMN_DEFS : COLUMN_DEFS;
  return props.columns
    .filter(key => key !== 'actions' && defs[key])
    .map(key => defs[key] as GaiaDataColumn);
});

/** One 34px slot per inline button plus the "⋯", or a single slot in Office. */
const actionWidth = computed(() =>
  isOfficeAddinHost() ? 46 : 34 * (Math.min(props.inlineActionCount, props.actions.length) + 1) + 12
);

const showColumn = (key: FileVaultColumnKey): boolean => props.columns.includes(key);

// ── Sorting ─────────────────────────────────────────────────────────────────
//
// A folder listing sorts on the server, folders, contracts and files together
// as one sequence. The choice lives in the vault store: refreshes and
// navigation keep it. Search results are one flat list and still sort here,
// over the rows in memory.

type SortColumnKey = 'name' | 'type' | 'owner' | 'lastModified' | 'state';
type ColumnSort = { key: SortColumnKey; direction: 'asc' | 'desc' };

const LISTING_SORT_KEY: Record<SortColumnKey, VaultListingSort['key']> = {
  name: 'name',
  type: 'type',
  owner: 'owner',
  // The backend sorts a never-updated item by its creation time, exactly
  // what the column shows.
  lastModified: 'updated_at',
  state: 'contract_state',
};

const searchSort = ref<ColumnSort | null>(null);

const activeSort = computed<ColumnSort | null>(() => {
  if (props.isSearchResults) return searchSort.value;
  const sort = vaultStore.listingSort;
  if (!sort) return null;
  const key = (Object.keys(LISTING_SORT_KEY) as SortColumnKey[]).find(
    column => LISTING_SORT_KEY[column] === sort.key
  );
  return key ? { key, direction: sort.direction } : null;
});

function handleSort(column: string): void {
  const key = column as SortColumnKey;
  const current = activeSort.value;
  const next: ColumnSort =
    current?.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' };
  if (props.isSearchResults) {
    searchSort.value = next;
    return;
  }
  vaultStore
    .setListingSort({ key: LISTING_SORT_KEY[next.key], direction: next.direction })
    .catch((error: unknown) => {
      logger.error('Failed to sort the folder:', error);
      message.error('Failed to sort the folder');
    });
}

/** The state a row shows: a contract's contract state. Folders and plain files have none. */
function rowState(item: VaultItem): ContractIconState | null {
  return item.type === 'contract' ? (item.contractStates?.contract_state ?? null) : null;
}

/** The value a search result sorts by, mirroring what the server compares. */
function searchSortValue(item: VaultItem, key: SortColumnKey): string | number {
  switch (key) {
    case 'lastModified':
      return item.lastModified ? Date.parse(item.lastModified) : 0;
    case 'type':
      return (item.typeLabel ?? '').toLowerCase();
    case 'owner':
      return (item.owner_user?.email ?? '').toLowerCase();
    case 'name':
      return item.name.toLowerCase();
    case 'state': {
      // Worst first ascending, like the server.
      const state = rowState(item);
      return state ? CONTRACT_ICON_STATES.indexOf(state) : -1;
    }
  }
}

const sortedItems = computed<VaultItem[]>(() => {
  if (!props.isSearchResults || !searchSort.value) return props.items;
  const { key } = searchSort.value;
  const factor = searchSort.value.direction === 'asc' ? 1 : -1;
  return [...props.items].sort((a, b) => {
    const left = searchSortValue(a, key);
    const right = searchSortValue(b, key);
    // Rows without a state come last in both directions, as on the server.
    if (key === 'state' && (left === -1 || right === -1)) {
      return (left === -1 ? 1 : 0) - (right === -1 ? 1 : 0);
    }
    if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;
    return String(left).localeCompare(String(right)) * factor;
  });
});

// ── Pagination ──────────────────────────────────────────────────────────────
//
// A folder listing pages on the server: `pagination` says which page `items`
// is, and a page change goes back up as `update:pagination`. Without it
// (search results) the whole result set is in `items` and is paged here.

const localPage = ref(1);
const localPageSize = ref(25);

const pageState = computed(
  () =>
    props.pagination ?? {
      page: localPage.value,
      pageSize: localPageSize.value,
      total: props.items.length,
    }
);

const pagedItems = computed<VaultItem[]>(() => {
  if (props.pagination) return sortedItems.value;
  const start = (localPage.value - 1) * localPageSize.value;
  return sortedItems.value.slice(start, start + localPageSize.value);
});

function changePage(page: number, pageSize: number): void {
  if (props.pagination) {
    emit('update:pagination', { page, pageSize });
    return;
  }
  localPage.value = page;
  localPageSize.value = pageSize;
}

// A new in-memory result set (a new search) starts at its first page.
watch(
  () => props.items,
  () => {
    if (!props.pagination) localPage.value = 1;
  }
);

// ── Selection ───────────────────────────────────────────────────────────────

/** System directories can never be selected, so they never count toward all. */
/**
 * System directories never take part in a selection, nor does a converting
 * file, which every bulk action would be refused on.
 */
function isSelectable(row: VaultItem): boolean {
  return !isSystemDirectory(row) && !row.converting;
}

const selectableKeys = computed(() =>
  props.items.filter(item => isSelectable(item)).map(item => item.key)
);

const allSelected = computed(
  () =>
    selectableKeys.value.length > 0 &&
    selectableKeys.value.every(key => props.selectedKeys.includes(key))
);

const someSelected = computed(
  () => !allSelected.value && selectableKeys.value.some(key => props.selectedKeys.includes(key))
);

function toggleSelectAll(checked: boolean): void {
  handleUpdateChecked(checked ? [...selectableKeys.value] : []);
}

function toggleRow(row: VaultItem, checked: boolean): void {
  if (!isSelectable(row)) return;
  handleUpdateChecked(
    checked ? [...props.selectedKeys, row.key] : props.selectedKeys.filter(key => key !== row.key)
  );
}

// ── Inline delete confirm ───────────────────────────────────────────────────

/** The row whose delete was asked for; its strip replaces the row until answered. */
const confirmKey = ref<string | null>(null);

function askDelete(row: VaultItem): void {
  confirmKey.value = row.key;
}

async function confirmDelete(row: VaultItem): Promise<void> {
  confirmKey.value = null;
  await handleDeleteItem(row);
}

const DELETE_CONSEQUENCE: Record<VaultItem['type'], string> = {
  directory: 'The folder and everything inside it, contracts included, is removed.',
  contract: 'The contract and all of its files are removed.',
  file: 'This file and all of its versions are removed.',
};

function deleteBlocked(row: VaultItem): boolean {
  return isSystemDirectory(row) || actionBlock(row, 'delete') !== null;
}

function previewReady(row: VaultItem): boolean {
  return (
    row.type === 'file' &&
    !isArchive(row.contentType, row.name) &&
    row.previewStatus === PreprocessingTaskStatus.DONE
  );
}

function formatModified(value: string | null | undefined): { short: string; full: string } {
  if (!value) return { short: '', full: '' };
  const date = new Date(value);
  return { short: date.toLocaleDateString(), full: date.toLocaleString() };
}

// Handle row selection (for clicking on row area, not file name)
function handleRowSelection(row: VaultItem): void {
  // The checkbox is disabled for unselectable rows; row-area clicks must not
  // bypass that.
  if (!isSelectable(row)) return;

  const key = row.key;
  const currentSelection = [...props.selectedKeys];

  if (currentSelection.includes(key)) {
    // If already selected, deselect it
    emit(
      'selection-change',
      currentSelection.filter(k => k !== key)
    );
  } else {
    // If not selected, add it to selection
    emit('selection-change', [...currentSelection, key]);
  }
}

// Handle selection changes
function handleUpdateChecked(keys: string[]): void {
  // Update store for direct usage (file vault page)
  vaultStore.setSelectedItems(keys);
  // Also emit event for parent components (modal usage)
  emit('selection-change', keys);
}

// Handle individual item deletion
async function handleDeleteItem(item: VaultItem): Promise<void> {
  try {
    if (item.type === 'file') {
      await vaultStore.deleteFile(item.key.replace('file-', ''));
    } else if (item.type === 'contract') {
      await vaultStore.deleteContract(item.key.replace('contract-', ''));
    } else {
      await vaultStore.deleteDirectory(item.key.replace('dir-', ''));
    }

    message.success(`Successfully deleted ${item.name}`);

    // Refresh the current directory to reflect the changes
    await refreshCurrentDirectory();
  } catch (error) {
    logger.error('Failed to delete item:', error);
    message.error(
      `Failed to delete ${item.name}: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

// Drag and drop handlers
function handleDragStart(event: DragEvent, item: VaultItem): void {
  if (props.disableDrag) {
    event.preventDefault();
    return;
  }

  // Skip drag if the item clicked was a directory we're currently in
  if (item.key === `dir-${vaultStore.currentDirectoryUid}`) {
    event.preventDefault();
    return;
  }

  // A converting file can't be moved at all, nor can anything without ADMIN.
  if (actionBlock(item, 'move')) {
    event.preventDefault();
    return;
  }

  // System directories can never be moved.
  if (isSystemDirectory(item)) {
    event.preventDefault();
    return;
  }

  // Prevent event bubbling to avoid conflicts with other handlers
  event.stopPropagation();

  // Clear any existing timeout
  if (dragTimeoutId.value) {
    clearTimeout(dragTimeoutId.value);
    dragTimeoutId.value = null;
  }

  // Set dragged item immediately
  draggedItem.value = item;

  // Add a small delay before setting isDragging to true to avoid triggering immediate UI updates
  // that might interfere with the drag operation
  setTimeout(() => {
    isDragging.value = true;
  }, 50);

  // Set drag data
  if (event.dataTransfer) {
    // Set a required format to ensure drag works across browsers
    event.dataTransfer.effectAllowed = 'move';

    // Some browsers require text/plain data to be set for drag to work
    try {
      const dragData = JSON.stringify({
        key: item.key,
        type: item.type,
        name: item.name,
      });

      // First set the simplest format that's most likely to succeed
      event.dataTransfer.setData('text', item.name);

      // Then try the more complex formats
      try {
        event.dataTransfer.setData('text/plain', dragData);
        event.dataTransfer.setData('application/json', dragData);
      } catch (complexError) {
        logger.warn('Could not set complex data formats:', complexError);
      }
    } catch (error) {
      logger.error('Error setting drag data:', error);
      // Ultimate fallback
      try {
        event.dataTransfer.setData('text', item.key);
      } catch (finalError) {
        logger.error('All drag data formats failed:', finalError);
      }
    }

    // Cross-pane payload (e.g. dropping files onto a review analysis). The
    // internal move-drag keeps reading draggedItem, so this changes nothing
    // for moves. Dragging a selected file carries the whole file selection.
    if (item.type === 'file') {
      const keys = props.selectedKeys.includes(item.key)
        ? props.selectedKeys.filter(key => key.startsWith('file-'))
        : [item.key];
      setVaultFileDragData(
        event,
        keys.flatMap(key => {
          const match = props.items.find(candidate => candidate.key === key);
          // A file without a revision (still uploading) has nothing to drop.
          if (!match?.revisionUid) return [];
          return [{ revisionUid: match.revisionUid, fileName: match.name }];
        })
      );
    }

    // Create ghost element for drag image - do this with a try/catch
    try {
      // Create the element with all styles directly applied
      const ghostElement = document.createElement('div');
      ghostElement.textContent = item.name;
      ghostElement.style.cssText = `
        padding: 5px 10px;
        background-color: ${gaiaAccent.tint};
        border: 1px solid ${gaiaAccent.base};
        border-radius: 4px;
        position: fixed;
        top: -1000px;
        left: 0;
        z-index: 9999;
        pointer-events: none;
        font-family: sans-serif;
        font-size: 14px;
        max-width: 200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `;

      // Append to body and force layout calculation
      document.body.appendChild(ghostElement);
      ghostElement.getBoundingClientRect(); // Force layout calculation

      // Set the drag image with a neutral offset
      event.dataTransfer.setDragImage(ghostElement, 10, 10);

      // Clean up the ghost element after a delay
      setTimeout(() => {
        if (document.body.contains(ghostElement)) {
          document.body.removeChild(ghostElement);
        }
      }, 200);
    } catch (error) {
      logger.error('Error setting drag image:', error);
      // If setting drag image fails, at least the drag should still work
    }
  } else {
    logger.warn('dataTransfer not available in dragStart event');
  }
}

function handleDragEnd(): void {
  // Clear any pending drag timeout
  if (dragTimeoutId.value) {
    clearTimeout(dragTimeoutId.value);
    dragTimeoutId.value = null;
  }

  // Set a small timeout before resetting isDragging to avoid UI flicker
  // This ensures that if a drop happens, the drop handlers have time to process
  dragTimeoutId.value = window.setTimeout(() => {
    // Reset all drag-related state
    isDragging.value = false;
    draggedItem.value = null;
    dropTargetKey.value = null;
    homeDropZoneActive.value = false;
    dragTimeoutId.value = null;

    // Clean up any data-drop-target attributes that might be left
    for (const element of document.querySelectorAll('[data-drop-target="true"]')) {
      element.removeAttribute('data-drop-target');
    }
  }, 50);
}

async function handleDrop(_event: DragEvent, targetItem: VaultItem): Promise<void> {
  // Reset state immediately to avoid UI issues
  dropTargetKey.value = null;
  showHomeDropZone.value = false;

  // Only a folder takes a drop; the backend checks WRITE on it.
  if (targetItem.type !== 'directory') {
    logger.warn('Drop target is not a directory');
    return;
  }

  if (!draggedItem.value) {
    logger.warn('No item being dragged when drop occurred');
    return;
  }

  // Don't allow dropping on itself
  if (draggedItem.value.key === targetItem.key) {
    logger.warn('Cannot drop on self');
    return;
  }

  // A directory cannot be dropped into its own subtree, but the flat list has
  // no ancestry info to check that client-side — the backend rejects such moves.

  // Store the item before resetting state
  const itemToMove = { ...draggedItem.value };
  const currentDirectoryUid = vaultStore.currentDirectoryUid;

  // Reset dragged item immediately to prevent issues if there's an error
  isDragging.value = false;
  draggedItem.value = null;

  try {
    const targetDirectoryUid = targetItem.key.replace('dir-', '');

    await vaultStore.bulkMoveVaultItems(groupVaultKeys([itemToMove.key]), targetDirectoryUid);

    // Emit an event that the item was moved
    emit('item-moved', itemToMove, targetItem.key, currentDirectoryUid);

    // Refresh to show the updated structure
    await refreshCurrentDirectory();
  } catch (error) {
    logger.error('Failed to move item:', error);
    // Show error message
    message.error(
      `Failed to move item: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

// Handle dragover on Home drop zone
function handleHomeDragOver(): void {
  // e.preventDefault() moved to template with @dragover.prevent
  homeDropZoneActive.value = true;
}

// Handle dragleave on Home drop zone
function handleHomeDragLeave(): void {
  homeDropZoneActive.value = false;
}

// Handle drop on Home drop zone
async function handleHomeDrop(): Promise<void> {
  // Reset state immediately to avoid UI issues
  homeDropZoneActive.value = false;
  showHomeDropZone.value = false;
  dropTargetKey.value = null;

  if (!draggedItem.value) {
    logger.warn('No item being dragged when drop occurred');
    return;
  }

  if (!homeDirectory.value) {
    logger.warn('Home directory could not be resolved for drop');
    return;
  }

  // Reset dragged item immediately to prevent issues if there's an error
  isDragging.value = false;
  const tempDraggedItem = { ...draggedItem.value };
  const currentDirectoryUid = vaultStore.currentDirectoryUid;
  draggedItem.value = null;

  try {
    const rootDirectoryUid = homeDirectory.value.key.replace('dir-', '');

    await vaultStore.bulkMoveVaultItems(groupVaultKeys([tempDraggedItem.key]), rootDirectoryUid);

    // Emit an event that the item was moved
    emit('item-moved', tempDraggedItem, homeDirectory.value.key, currentDirectoryUid);

    // Refresh the directories to show the updated structure
    await refreshCurrentDirectory();
  } catch (error) {
    logger.error('Failed to move item to Home:', error);
    message.error(
      `Failed to move item: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

// Refresh the current directory to reflect changes (move/delete).
async function refreshCurrentDirectory(): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
  await vaultStore.getVaultDirectory(vaultStore.currentDirectoryUid || undefined);
}
// ── Row helpers used by the template ───────────────────────────────────────

function rowIcon(row: VaultItem): Record<string, unknown> {
  if (row.type === 'directory') return IconFolder;
  if (row.type === 'contract') return IconDocumentLock;
  return isArchive(row.contentType, row.name) ? IconArchiveFile : IconDocument;
}

/**
 * Clicking anywhere on the row, name included, toggles selection. Clicks that
 * landed on a control have their own meaning, so they are left alone — this
 * is the same guard the naive table's `rowProps` carried.
 */
function handleRowAreaClick(event: MouseEvent, row: VaultItem): void {
  const target = event.target as HTMLElement;
  if (
    target.closest('input[type="checkbox"]') ||
    target.closest('.n-checkbox') ||
    target.closest('button') ||
    target.tagName === 'INPUT'
  ) {
    return;
  }
  event.stopPropagation();
  handleRowSelection(row);
}

/** Only a folder the caller can WRITE to accepts a drop; everything else ignores the drag. */
function isDropTarget(row: VaultItem): boolean {
  return row.type === 'directory' && allows(row.permissions, ACCESS_PERMISSION_WRITE);
}

function onRowDragOver(event: DragEvent, row: VaultItem): void {
  if (isOsFileDrag(event) || !isDropTarget(row)) return;
  event.preventDefault();
  dropTargetKey.value = row.key;
}

function onRowDragLeave(row: VaultItem): void {
  if (dropTargetKey.value === row.key) dropTargetKey.value = null;
}

function onRowDrop(event: DragEvent, row: VaultItem): void {
  if (isOsFileDrag(event) || !isDropTarget(row)) return;
  event.preventDefault();
  void handleDrop(event, row);
}

// ── Drop-to-upload: files and folders from the OS ──────────────────────────
//
// A folder's or contract's name (icon + name, padded) is a target of its own;
// the rest of the table, rows included, stands for the current folder. The
// table counts enter/leave pairs, since the events fire on every child it
// crosses. What the drop holds is only readable on drop, so the targets light
// up regardless of size; FileVault decides what to do with the count.

const fileDragActive = ref(false);
const fileDropRow = ref<VaultItem | null>(null);
let fileDragDepth = 0;

/** Folders and contracts are name targets — writable or not, so a refused one says so. */
function isFileDropName(row: VaultItem): boolean {
  return props.fileDrop && (row.type === 'directory' || row.type === 'contract');
}

/** Why files can't be dropped onto this folder or contract; null when they can. */
function fileDropBlock(row: VaultItem): string | null {
  if (row.converting) return 'Still being converted';
  if (isSystemDirectory(row)) return 'Managed by the platform';
  return deniedReason(row.permissions, ACCESS_PERMISSION_WRITE);
}

/** The hovered name's refusal, if it refuses. */
const fileDropRowBlock = computed(() =>
  fileDropRow.value ? fileDropBlock(fileDropRow.value) : null
);

/**
 * Why the spot under the pointer refuses the drop, keyed by what it is. The
 * browser sends no drop event where the cursor says "not allowed", so the
 * reason is shown on arrival instead — once per spot per drag.
 */
const fileDropRefusal = computed<{ key: string; text: string } | null>(() => {
  if (!fileDragActive.value) return null;
  const row = fileDropRow.value;
  if (row) {
    const block = fileDropRowBlock.value;
    return block
      ? { key: row.key, text: `${block} — files can’t be added to “${row.name}”` }
      : null;
  }
  return props.fileDropHereBlocked ? { key: 'here', text: props.fileDropHereBlocked } : null;
});

const warnedFileDropKeys = new Set<string>();

watch(fileDropRefusal, refusal => {
  if (!refusal || warnedFileDropKeys.has(refusal.key)) return;
  warnedFileDropKeys.add(refusal.key);
  message.warning(refusal.text);
});

/** The table stands for the current folder only while no name is hovered. */
const fileDropHere = computed(
  () => fileDragActive.value && fileDropRow.value === null && props.fileDropHereBlocked === null
);

function resetFileDrag(): void {
  warnedFileDropKeys.clear();
  fileDragDepth = 0;
  fileDragActive.value = false;
  fileDropRow.value = null;
}

function onNameDragOver(event: DragEvent, row: VaultItem): void {
  if (!isOsFileDrag(event) || !isFileDropName(row)) return;
  fileDropRow.value = row;
}

function onNameDragLeave(row: VaultItem): void {
  if (fileDropRow.value?.key === row.key) fileDropRow.value = null;
}

function onTableDragEnter(event: DragEvent): void {
  if (!props.fileDrop || !isOsFileDrag(event)) return;
  fileDragDepth++;
  fileDragActive.value = true;
}

function onTableDragOver(event: DragEvent): void {
  if (!props.fileDrop || !isOsFileDrag(event)) return;
  // Always prevented, so a drop the vault refuses is not opened by the browser.
  event.preventDefault();
  if (event.dataTransfer) {
    const accepted = fileDropRow.value ? fileDropRowBlock.value === null : fileDropHere.value;
    event.dataTransfer.dropEffect = accepted ? 'copy' : 'none';
  }
}

function onTableDragLeave(event: DragEvent): void {
  if (!props.fileDrop || !isOsFileDrag(event)) return;
  fileDragDepth = Math.max(0, fileDragDepth - 1);
  if (fileDragDepth === 0) resetFileDrag();
}

function onTableDrop(event: DragEvent): void {
  if (!props.fileDrop || !isOsFileDrag(event)) return;
  event.preventDefault();
  const row = fileDropRow.value;
  const rowBlock = fileDropRowBlock.value;
  const here = fileDropHere.value;
  resetFileDrag();
  if (!event.dataTransfer || (row ? rowBlock : !here)) return;
  const target: VaultFileDropTarget = row
    ? {
        kind: row.type === 'contract' ? 'contract' : 'directory',
        uid: row.key.replace(/^(dir|contract)-/, ''),
        name: row.name,
      }
    : { kind: 'here' };
  // Read before anything is awaited: the drop's items are gone after this turn.
  void readDroppedFiles(event.dataTransfer).then(files => {
    emit('files-dropped', target, files);
  });
}

// A drag that ends outside the table (dropped elsewhere, or cancelled) never
// sends the table its last dragleave.
onMounted(() => {
  window.addEventListener('drop', resetFileDrag);
  window.addEventListener('dragend', resetFileDrag);
});
onBeforeUnmount(() => {
  window.removeEventListener('drop', resetFileDrag);
  window.removeEventListener('dragend', resetFileDrag);
});

const accentColor = gaiaAccent.base;
const dropSurface = gaiaAccent.wash;
const inkColor = gaiaNeutral.ink;
const subtleColor = gaiaNeutral.textSubtle;
const tertiaryColor = gaiaNeutral.textTertiary;
const badgeBorder = gaiaNeutral.border;
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceSoft;
const alertColor = gaiaStatus.alert;
const alertSurface = gaiaStatus.alertSurface;
const warningColor = STATUS_TONES.warning.color;
const warningSurface = STATUS_TONES.warning.bg;
// System folders keep the old design's amber icon so they read as the
// platform's at a glance, before the badge is close enough to read.
const systemColor = gaiaStatus.warning;
const alertHover = gaiaStatus.alertHover;
const blankColor = gaiaNeutral.textBlank;
const nameSize = gaiaFontSize.row;
const nameWeight = String(gaiaFontWeight.medium);
const badgeSize = gaiaFontSize.nano;
const badgeWeight = String(gaiaFontWeight.semibold);
</script>

<template>
  <div
    class="file-list"
    :class="{
      'is-dragging': isDragging,
      'file-list--office': isOfficeAddinHost(),
    }"
  >
    <!-- Only while something is being dragged, and only below the root: the
         way back out of a folder without navigating there first. -->
    <div
      v-if="canDropToHome && showHomeDropZone"
      class="home-drop-zone"
      :class="{ active: homeDropZoneActive }"
      @dragover.prevent="handleHomeDragOver"
      @dragleave="handleHomeDragLeave"
      @drop.prevent="handleHomeDrop"
    >
      <n-icon :component="IconHome" :size="16" />
      <!-- The trail's first segment is named 'Home' only when it truly is the
           vault root; on trimmed deep links it is a regular folder. -->
      <span>Drop here to move to {{ homeDirectory?.name ?? 'Home' }}</span>
    </div>

    <gaia-data-table
      :class="{ 'file-drop-here': fileDropHere }"
      :columns="tableColumns"
      lead-column
      :lead-width="34"
      :action-width="actionWidth"
      :sort-key="activeSort?.key"
      :sort-direction="activeSort?.direction ?? 'asc'"
      :sort-active="activeSort !== null"
      @sort="handleSort"
      @dragenter="onTableDragEnter"
      @dragover="onTableDragOver"
      @dragleave="onTableDragLeave"
      @drop="onTableDrop"
    >
      <template #head-lead>
        <n-checkbox
          :checked="allSelected"
          :indeterminate="someSelected"
          :disabled="selectableKeys.length === 0"
          aria-label="Select all"
          @update:checked="toggleSelectAll"
        />
      </template>

      <template #default="{ rowStyle }">
        <!-- Loading with nothing to show yet: the header stays live, the body
             shimmers. With rows already on screen (background refresh) the rows
             stay put instead. -->
        <gaia-table-skeleton
          v-if="props.loading && props.items.length === 0"
          :row-style="rowStyle"
          :cells="skeletonCells"
          :rows="5"
          :row-height="48"
          lead
        />

        <gaia-empty-state
          v-else-if="!props.loading && props.items.length === 0"
          :title="isSearchResults ? 'Nothing matches your search' : 'This folder is empty'"
        >
          <template #icon>
            <n-icon :component="isSearchResults ? IconSearch : IconFolderOpen" :size="18" />
          </template>
          <template v-if="isSearchResults">
            Search covers folder, contract and file names across the whole vault.
          </template>
          <template v-else-if="!isOfficeAddinHost()">
            Use the “New” button to add files and folders here.
          </template>
        </gaia-empty-state>

        <template v-for="row in pagedItems" v-else :key="row.key">
          <gaia-table-row-state
            v-if="confirmKey === row.key"
            variant="confirm"
            :title="`Delete “${row.name}”?`"
            @cancel="confirmKey = null"
            @confirm="confirmDelete(row)"
          >
            {{ DELETE_CONSEQUENCE[row.type] }}
          </gaia-table-row-state>

          <!-- A directory the user can write to is also a drop target: dropping
               onto it moves the dragged selection inside. -->
          <div
            v-else
            class="row"
            :class="{ 'drop-target': dropTargetKey === row.key }"
            :style="rowStyle"
            @click="handleRowAreaClick($event, row)"
            @dblclick.stop="emit('item-dblclick', row)"
            @dragover="onRowDragOver($event, row)"
            @dragleave="onRowDragLeave(row)"
            @drop="onRowDrop($event, row)"
          >
            <div class="cell">
              <n-checkbox
                :checked="props.selectedKeys.includes(row.key)"
                :disabled="!isSelectable(row)"
                :aria-label="`Select ${row.name}`"
                @update:checked="(checked: boolean) => toggleRow(row, checked)"
              />
            </div>

            <!-- Cells follow the column order in fileVaultColumns.ts.
                 Only contracts have a status; folders and plain files leave it blank. -->
            <div v-if="showColumn('state') && !isOfficeAddinHost()" class="cell">
              <gaia-contract-state-cell
                v-if="row.type === 'contract'"
                :states="row.contractStates ?? null"
              />
            </div>

            <!-- `tree-node-content` / `file-name-clickable` are tour anchors —
                 tours/vault.ts finds the demo row through them. -->
            <div
              class="cell name-cell tree-node-content"
              :class="
                row.type === 'directory'
                  ? 'tree-node-content--directory'
                  : 'tree-node-content--file'
              "
            >
              <!-- A folder's or contract's drop target for files from the OS:
                   just the icon and the name, padded into a small box. -->
              <span
                class="drop-name"
                :class="
                  fileDropRow?.key === row.key
                    ? fileDropRowBlock
                      ? 'drop-name--blocked'
                      : 'drop-name--active'
                    : undefined
                "
                @dragover="onNameDragOver($event, row)"
                @dragleave="onNameDragLeave(row)"
              >
                <n-icon
                  :component="rowIcon(row)"
                  :size="17"
                  :color="
                    isSystemDirectory(row)
                      ? systemColor
                      : row.type === 'directory'
                        ? accentColor
                        : subtleColor
                  "
                  class="row-icon"
                />
                <span
                  class="file-name-clickable"
                  :title="row.name"
                  :draggable="!disableDrag && !actionBlock(row, 'move')"
                  @dragstart="handleDragStart($event, row)"
                  @dragend="handleDragEnd"
                  >{{ row.name }}</span
                >
              </span>

              <span
                v-if="isSystemDirectory(row)"
                class="badge system"
                title="Managed by the platform"
              >
                System
              </span>
              <component :is="renderFolderCountBadge(row)" v-if="renderFolderCountBadge(row)" />
              <span
                v-if="row.converting"
                class="badge converting"
                :title="`${conversionPhase(row) === 'importing' ? 'Being read in' : 'Being analyzed'} on its way to becoming a contract. Rename, move and delete wait until it is done.`"
              >
                <gaia-spinner :size="10" :stroke-width="24" />
                {{ conversionPhase(row) === 'importing' ? 'Importing' : 'Analyzing' }}
              </span>
              <!-- How many files the contract holds; hovering lists them. -->
              <contract-files-popover
                v-if="row.type === 'contract' && row.contractFileCount !== undefined"
                :contract-uid="row.key.replace('contract-', '')"
              >
                <span class="badge files" @click.stop @dblclick.stop>
                  {{ row.contractFileCount }}
                  <n-icon :component="IconDocuments" :size="11" />
                </span>
              </contract-files-popover>
              <span
                v-if="row.isDraft"
                class="badge"
                title="Being drafted from a template. Open it to continue editing."
              >
                Draft
              </span>
              <gaia-version-badge
                v-if="row.type === 'file' && row.versionNumber && row.versionNumber > 1"
                :version="row.versionNumber"
                title="Has multiple versions"
              />
              <component
                :is="renderConversionIndicator(row)"
                v-if="renderConversionIndicator(row)"
              />
              <gaia-tooltip v-if="processingIssue(row)">
                <template #trigger>
                  <span class="badge" :class="processingIssue(row)?.level">
                    {{ processingIssue(row)?.level === 'error' ? 'Error' : 'Warning' }}
                  </span>
                </template>
                <n-flex vertical :size="2">
                  <span v-for="line in processingIssue(row)?.lines" :key="line">{{ line }}</span>
                </n-flex>
              </gaia-tooltip>
            </div>

            <div v-if="showColumn('type') && !isOfficeAddinHost()" class="cell">
              <gaia-text truncate size="control" tone="tertiary">
                {{ vaultTypeLabel(row.typeLabel) }}
              </gaia-text>
            </div>

            <div v-if="showColumn('lastModified') && !isOfficeAddinHost()" class="cell">
              <gaia-tooltip v-if="row.lastModified">
                <template #trigger>
                  <gaia-text
                    truncate
                    size="control"
                    tone="tertiary"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                    >{{ formatModified(row.lastModified).short }}</gaia-text
                  >
                </template>
                {{ formatModified(row.lastModified).full }}
              </gaia-tooltip>
            </div>

            <!-- Just the mark, per the mock: the name lives in its tooltip.
                 A file or contract with no owner gets the dashed "unassigned"
                 placeholder; folders show nothing. -->
            <div v-if="showColumn('owner')" class="cell center">
              <user-initials-avatar
                v-if="row.owner_user"
                :user="{
                  full_name: row.owner_user.full_name,
                  email: row.owner_user.email,
                  uid: row.owner_user.uid,
                }"
                :size="isOfficeAddinHost() ? 'xs' : 'sm'"
                show-tooltip
              />
              <span v-else-if="row.type !== 'directory'" class="unassigned" title="Unassigned">
                <n-icon :component="IconUser" :size="11" />
              </span>
            </div>

            <!-- The "⋯" is deliberately the FIRST button here: the product tour
                 opens the row menu by taking the first button in this cell, and
                 the inline icons fire real actions like download. -->
            <n-flex class="actions-cell" align="center" justify="end" :size="4" :wrap="false">
              <gaia-dropdown
                v-if="isOfficeAddinHost()"
                v-show="getOfficeActionOptions(row).length > 0"
                :options="getOfficeActionOptions(row)"
                :menu-props="getCompactActionsMenuProps"
                placement="bottom-end"
                @select="(key: string | number) => handleOfficeActionSelect(key, row)"
              >
                <gaia-icon-button :dimension="26" quaternary title="More actions" @click.stop>
                  <n-icon :component="IconMore" :size="14" />
                </gaia-icon-button>
              </gaia-dropdown>

              <template v-else>
                <gaia-dropdown
                  v-if="splitRowActions(row).menuOptions.length > 0"
                  :options="splitRowActions(row).menuOptions"
                  :menu-props="getCompactActionsMenuProps"
                  placement="bottom-end"
                  @select="(key: string | number) => handleOfficeActionSelect(key, row)"
                >
                  <gaia-icon-button :dimension="28" quaternary title="More actions" @click.stop>
                    <n-icon :component="IconMore" :size="16" />
                  </gaia-icon-button>
                </gaia-dropdown>

                <template v-for="action in splitRowActions(row).buttonActions" :key="action.key">
                  <gaia-tooltip v-if="action.key === 'preview'">
                    <template #trigger>
                      <span>
                        <gaia-icon-button
                          :dimension="28"
                          quaternary
                          class="inline-action"
                          :class="{
                            dim: !previewReady(row) && !isArchive(row.contentType, row.name),
                          }"
                          @click.stop="
                            isArchive(row.contentType, row.name)
                              ? emit('unpack', row)
                              : previewReady(row) && emit('preview', row)
                          "
                        >
                          <n-icon
                            :component="
                              isArchive(row.contentType, row.name) ? IconArchive : IconEye
                            "
                            :size="16"
                          />
                        </gaia-icon-button>
                      </span>
                    </template>
                    {{
                      isArchive(row.contentType, row.name)
                        ? 'Unpack'
                        : previewReady(row)
                          ? 'Preview'
                          : 'Preview not ready'
                    }}
                  </gaia-tooltip>

                  <gaia-tooltip v-else-if="action.key === 'delete'">
                    <template #trigger>
                      <span>
                        <gaia-icon-button
                          :dimension="28"
                          quaternary
                          class="inline-action danger"
                          :disabled="deleteBlocked(row)"
                          @click.stop="askDelete(row)"
                        >
                          <n-icon :component="IconDelete" :size="16" />
                        </gaia-icon-button>
                      </span>
                    </template>
                    {{ actionBlock(row, 'delete') ?? 'Delete' }}
                  </gaia-tooltip>

                  <gaia-tooltip v-else>
                    <template #trigger>
                      <span>
                        <gaia-icon-button
                          :dimension="28"
                          quaternary
                          class="inline-action"
                          :disabled="action.option.disabled === true"
                          @click.stop="handleOfficeActionSelect(String(action.option.key), row)"
                        >
                          <n-icon :component="ACTION_ICONS[action.key]" :size="16" />
                        </gaia-icon-button>
                      </span>
                    </template>
                    {{ actionBlock(row, action.key) ?? action.option.label }}
                  </gaia-tooltip>
                </template>
              </template>
            </n-flex>
          </div>
        </template>
      </template>

      <template v-if="props.items.length > 0" #footer>
        <gaia-pagination
          :page="pageState.page"
          :page-size="pageState.pageSize"
          :total="pageState.total"
          :page-sizes="[25, 50, 100]"
          @update:page="(page: number) => changePage(page, pageState.pageSize)"
          @update:page-size="(size: number) => changePage(1, size)"
        />
      </template>
    </gaia-data-table>
  </div>
</template>

<style scoped>
.file-list {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.row {
  display: grid;
  align-items: center;
  gap: 16px;
  min-height: 48px;
  padding: 0 18px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.file-list--office .row {
  min-height: 34px;
  padding: 0 10px;
  gap: 10px;
}

/* The folder under the pointer during a drag, ringed rather than filled so the
   row's own contents stay readable while it is a target. */
.row.drop-target {
  background: v-bind(dropSurface);
  box-shadow: inset 0 0 0 1.5px v-bind(accentColor);
}

.cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.cell.center {
  justify-content: center;
}

/* A file nobody owns: an empty seat rather than a blank cell, so "unassigned"
   reads as a state someone can act on. */
.unassigned {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1.5px dashed v-bind(badgeBorder);
  border-radius: 50%;
  color: v-bind(blankColor);
}

.name-cell {
  gap: 10px;
  padding-right: 12px;
  overflow: hidden;
  /* Pulled left by .drop-name's padding, so the icon stays on its column. */
  margin-left: -6px;
}

/* Icon + name. Padded all the time so the drop ring has room inside the
   cell's clip without moving anything when it appears. */
.drop-name {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  overflow: hidden;
  padding: 3px 6px;
  border-radius: 7px;
}

/* A name that refuses the drop: dashed and muted, with the browser's
   not-allowed cursor; the reason comes as a toast when it is reached. */
.drop-name--blocked {
  box-shadow: none;
  outline: 1.5px dashed v-bind(badgeBorder);
  outline-offset: -1.5px;
}

.drop-name--active {
  background: v-bind(dropSurface);
  box-shadow: inset 0 0 0 1.5px v-bind(accentColor);
}

/* The whole table is the target for the current folder. */
.gaia-data-table.file-drop-here {
  box-shadow: 0 0 0 2px v-bind(accentColor);
}

.row-icon {
  flex: none;
}

.file-name-clickable {
  flex: 0 1 auto;
  min-width: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: v-bind(nameSize);
  font-weight: v-bind(nameWeight);
  color: v-bind(inkColor);
}

.badge {
  display: inline-flex;
  align-items: center;
  flex: none;
  height: 19px;
  padding: 0 7px;
  border: 1px solid v-bind(badgeBorder);
  border-radius: 7px;
  font-size: v-bind(badgeSize);
  font-weight: v-bind(badgeWeight);
  color: v-bind(subtleColor);
}

/* A version number is compared down the column, so it takes the mono face. */
.badge.files {
  gap: 3px;
  cursor: default;
}

.badge.converting {
  gap: 5px;
  cursor: default;
}

.badge.error {
  border-color: v-bind(alertColor);
  background: v-bind(alertSurface);
  color: v-bind(alertColor);
  cursor: default;
}

.badge.warning {
  border-color: v-bind(warningColor);
  background: v-bind(warningSurface);
  color: v-bind(warningColor);
  cursor: default;
}

.actions-cell {
  min-width: 0;
}

.inline-action.danger {
  color: v-bind(alertColor);
}

.inline-action.danger:hover {
  background: v-bind(alertHover);
}

/* Preview that is not ready yet: still present so the row's action set does
   not reflow when the preview finishes, but visibly inert. */
.inline-action.dim {
  opacity: 0.35;
  cursor: default;
}

.home-drop-zone {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 48px;
  margin-bottom: 10px;
  border: 1.5px dashed v-bind(badgeBorder);
  border-radius: 11px;
  color: v-bind(tertiaryColor);
  font-size: v-bind(nameSize);
  font-weight: v-bind(nameWeight);
}

.home-drop-zone.active {
  border-color: v-bind(accentColor);
  background: v-bind(dropSurface);
}
</style>
